import { useProjectStore } from '@talon/web/src/context';
import { Anchor, Box, Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { breadcrumbsFor } from './breadcrumbs';

export type BreadcrumbProps = {
  /** The record a detail page shows (an asset's or performer's name), as the last step */
  detail?: string;
};

/** The strip under the header that says where the user is and links back up (see breadcrumbsFor) */
export const Breadcrumb = observer(({ detail }: BreadcrumbProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const projectStore = useProjectStore();

  const crumbs = breadcrumbsFor(location.pathname, projectStore.currentProject?.name, detail);
  if (!crumbs) return null;

  return (
    <Box
      as="nav"
      aria-label="Breadcrumb"
      background="white"
      border={{ side: 'bottom', color: 'border-layout' }}
      pad={{ vertical: '10px', horizontal: 'medium' }}
      direction="row"
      gap="small"
      align="center"
      wrap
    >
      {crumbs.flatMap((crumb, i) => {
        const current = i === crumbs.length - 1;
        const label = current ? (
          <Text key={i} size="small" color="text-heading" weight={600} aria-current="page">
            {crumb.label}
          </Text>
        ) : crumb.to ? (
          <Anchor key={i} onClick={() => crumb.to && navigate(crumb.to)} color="text-meta">
            <Text size="small" color="text-meta" weight="normal">
              {crumb.label}
            </Text>
          </Anchor>
        ) : (
          <Text key={i} size="small" color="text-meta">
            {crumb.label}
          </Text>
        );
        // Box's gap only spaces its direct children, so the separators are siblings, not wrapped
        if (i === 0) return [label];
        const separator = (
          <Text key={`sep-${i}`} size="small" color="text-meta" aria-hidden>
            ›
          </Text>
        );
        return [separator, label];
      })}
    </Box>
  );
});
